/* @flow */
import RemindAPI from 'lib/RemindAPI';
import CurrentUser from 'auth/CurrentUser';
import Organization from 'entities/Organization';
import Organizations from 'collections/Organizations';
import OrganizationStore from 'stores/OrganizationStore';
import { dispatcher } from 'lib/Dispatcher';
import ToastActions from 'actions/ToastActions';
import organizationDefaultFragment from 'organizations/fragments/organizationDefaultFragment.gql';
import gql from 'graphql-tag';

import { ActionTypes } from '../constants/OrganizationConstants';

type UserInfo = {
  first_name: string,
  last_name: string,
  address: string,
}

const OrganizationActions = {
  /**
   * Get the organization from the store. If isn't available then load it from
   * the server.
   */
  getOrLoadOrganization(id: number): Promise<Organization> {
    const preloadedOrg = OrganizationStore.getOrganization(id);
    if (preloadedOrg) {
      return Promise.resolve(preloadedOrg);
    } else {
      return this.loadOrganization(id);
    }
  },

  /**
   * Fetch all the organizations with which the current user is affiliated.
   */
  async loadUserOrganizations(): Promise<void> {
    const query = gql`
      ${organizationDefaultFragment}

      query {
        me {
          id
          organizations {
            ...organizationDefault
          }
        }
      }`;

    const data = await RemindAPI.execute(query);

    const organizations = data.me.organizations
      .map(org => Organization.fromJSON(org));

    dispatcher.dispatch({
      type: ActionTypes.LOAD_USER_ORGANIZATIONS,
      organizations: Organizations.fromJSON(organizations),
    });
  },

  /**
   * Fetch the organization from the server.
   */
  async loadOrganization(id: number): Promise<Organization> {
    const query = gql`
      ${organizationDefaultFragment}
      query ($orgIds: [Int]!) {
        organizations(ids: $orgIds) {
           ...organizationDefault
        }
      }`;

    const orgIds = [id];
    const data = await RemindAPI.execute(query, { orgIds });

    const organization = Organization.fromJSON(data.organizations[0]);

    dispatcher.dispatch({
      type: ActionTypes.LOAD_ORGANIZATION,
      organization,
    });
    return organization;
  },

  createOrganization(attr: Object): Promise<Organization> {
    // don't hit api if user isn't logged in, since school picker is used on admin waitlist
    // and unauthed users may want to create a school if it's not there
    if (!CurrentUser.isSignedIn()) {
      return Promise.resolve(new Organization(attr));
    }
    return new Organization(attr).create();
  },

  addOrganization(organization: Organization) {
    dispatcher.dispatch({
      type: ActionTypes.LOAD_ORGANIZATION,
      organization,
    });
  },

  removeOrganization(orgId: number): void {
    dispatcher.dispatch({
      type: ActionTypes.REMOVE_ORGANIZATION,
      orgId,
    });
  },

  /**
   * Adds a set of users to an organization via the admin's direct add flow
   * @param {array} users an array of JSON objects with first_name, last_name and email
   * @return {Promise} for a job status object that can be pinged for results
   */
  addUsers(users: UserInfo[], role: string, orgId: number): Promise<void> {
    return RemindAPI.request(`/organizations/${orgId}/${role}`, 'POST', { users });
  },

  batchRequestTranscript(orgId: number, transcripts: {user_id: number, request_from_affected_user: boolean}[], note: string, inclChat: boolean, inclAnnouncements: boolean) {
    return RemindAPI.request(`/organizations/${orgId}/email_transcript/batch`, 'POST', {
      incl_announcements: inclAnnouncements,
      incl_chat: inclChat,
      transcripts,
      note,
    });
  },

  batchRemoveFromOrg(orgId: number, userIds: number[], reason: string): Promise<{}> {
    return RemindAPI.request(`/organizations/${orgId}/users/batch`, 'DELETE', { reason, user_ids: userIds });
  },

  batchBanFromOrg(orgId: number, userIds: number[], reason: string): Promise<{}> {
    ToastActions.showErrorToast('Not yet implemented'); // TODO
    return RemindAPI.request(`/organizations/${orgId}/users/ban/batch`, 'POST', { reason, user_ids: userIds });
  },

  reportUserAsAdmin(orgId: number, userId: number, reason: string, notes: string) {
    return RemindAPI.request(`/organizations/${orgId}/users/${userId}/report`,
                      'POST', { reason, notes });
  },
};

export default OrganizationActions;



// WEBPACK FOOTER //
// ./src/actions/OrganizationActions.js